import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { couponsService, type CreateCouponData } from '../api/coupons.service';

export type SaveCouponInput = {
  mode: 'create' | 'edit';
  editingId: string | null;
  formData: CreateCouponData;
};

function invalidateCoupons(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: queryKeys.coupons.all });
}

export function useCouponMutations() {
  const qc = useQueryClient();
  const onError = useErrorToast();

  const saveCoupon = useMutation({
    mutationFn: async (input: SaveCouponInput) => {
      const code = input.formData.code.trim().toUpperCase();
      if (input.mode === 'edit' && input.editingId) {
        return couponsService.update(input.editingId, {
          code,
          discountStrategy: input.formData.discountStrategy,
          value: input.formData.value,
          durationType: input.formData.durationType,
          durationMonths: input.formData.durationMonths ?? null,
          usageType: input.formData.usageType,
          affiliateId: input.formData.affiliateId ?? null,
          description: input.formData.description?.trim() ? input.formData.description.trim() : undefined,
          maxRedemptions: input.formData.maxRedemptions ?? null,
          validFrom: input.formData.validFrom || null,
          validUntil: input.formData.validUntil || null,
          planIds: input.formData.planIds ?? null,
        });
      }
      return couponsService.create({
        ...input.formData,
        code,
        usageType: input.formData.usageType || 'CAMPAIGN',
        affiliateId: input.formData.affiliateId ?? null,
      });
    },
    onSuccess: (_data, variables) => {
      invalidateCoupons(qc);
      toast.success(
        variables.mode === 'edit' ? 'Cupom atualizado com sucesso!' : 'Cupom criado com sucesso!'
      );
    },
    onError: (e) => onError(e, 'Erro ao salvar cupom'),
  });

  const deactivateCoupon = useMutation({
    mutationFn: (id: string) => couponsService.deactivate(id),
    onSuccess: () => {
      invalidateCoupons(qc);
      toast.success('Cupom desativado.');
    },
    onError: (e) => onError(e, 'Erro ao desativar cupom'),
  });

  const deleteCoupon = useMutation({
    mutationFn: (id: string) => couponsService.delete(id),
    onSuccess: () => {
      invalidateCoupons(qc);
      toast.success('Cupom excluído.');
    },
    onError: (e) => onError(e, 'Erro ao excluir cupom'),
  });

  return { saveCoupon, deactivateCoupon, deleteCoupon };
}
